import React, { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

/**
 * Cinematic custom cursor.
 * Hovering over `[data-cursor]` elements changes the cursor label.
 * Disabled on touch devices.
 */
export default function CustomCursor() {
  const [isTouch, setIsTouch] = useState(false);
  const [label, setLabel] = useState("");
  const [hovering, setHovering] = useState(false);
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const sx = useSpring(x, { stiffness: 350, damping: 30, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 350, damping: 30, mass: 0.4 });
  const ref = useRef(null);

  useEffect(() => {
    const touch = window.matchMedia("(hover: none)").matches;
    setIsTouch(touch);
    if (touch) return;
    document.body.classList.add("cursor-hidden");

    const move = (e) => { x.set(e.clientX); y.set(e.clientY); };
    const over = (e) => {
      const el = e.target.closest("[data-cursor]");
      if (el) { setHovering(true); setLabel(el.getAttribute("data-cursor") || ""); }
    };
    const out = (e) => {
      if (!e.relatedTarget || !e.relatedTarget.closest?.("[data-cursor]")) {
        setHovering(false); setLabel("");
      }
    };
    window.addEventListener("mousemove", move);
    document.addEventListener("mouseover", over);
    document.addEventListener("mouseout", out);
    return () => {
      document.body.classList.remove("cursor-hidden");
      window.removeEventListener("mousemove", move);
      document.removeEventListener("mouseover", over);
      document.removeEventListener("mouseout", out);
    };
  }, [x, y]);

  if (isTouch) return null;

  return (
    <>
      <motion.div
        ref={ref}
        aria-hidden
        style={{ x: sx, y: sy }}
        className="fixed left-0 top-0 z-[80] pointer-events-none"
      >
        <motion.div
          animate={{ scale: hovering ? 3.4 : 1, opacity: hovering ? 0.18 : 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 20 }}
          className="w-3 h-3 -translate-x-1/2 -translate-y-1/2 bg-ink-100 rounded-full"
        />
        {hovering && label && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute left-4 top-4 px-2 py-1 bg-rouge text-ink-100 text-[10px] tracking-[0.2em] uppercase whitespace-nowrap"
          >
            {label}
          </motion.div>
        )}
      </motion.div>
    </>
  );
}
